import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { errorStyle } from '../../../constants/styles';
import Button from '../../Button';
import { TextInput } from '../../Inputs';
import { useAuth } from '../AuthContextProvider';
import { AuthValidationError } from '../types';
import { toast } from 'react-toastify';

export interface ForgotPasswordFormProps {
  email: string;
}

type InputErrorKey = keyof ForgotPasswordFormProps;
const ForgotPasswordView: React.FC = () => {
  const { actions } = useAuth();
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<ForgotPasswordFormProps>({
    defaultValues: {
      email: '',
    },
  });

  const handlePasswordReset = async ({ email }: ForgotPasswordFormProps) => {
    setLoading(true);

    try {
      await actions.forgotPassword(email);

      toast(
        'Instructions have been sent to your email address on how to reset your password.',
        { type: 'success' }
      );

      reset();
    } catch (error) {
      if (error instanceof AuthValidationError) {
        toast(error.message, { type: 'error' });

        const errors = error.errors;
        const keys = Object.keys(errors) as InputErrorKey[];

        keys.forEach(errKey => {
          // @ts-ignore
          setError(errKey, { type: 'api', message: errors[errKey] as string });
        });
      } else {
        console.error(error);
      }
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center w-full">
      <div className="max-w-sm w-full">
        <form
          id="auth-forgot-password"
          onSubmit={handleSubmit(handlePasswordReset)}
        >
          <div className="my-6">
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: true,
                pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              }}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  type="email"
                  value={value}
                  placeholder="Email Address"
                  onChange={onChange}
                  invalid={!!errors.email}
                />
              )}
            />

            {errors.email && errors.email.type === 'required' && (
              <span className={errorStyle}>Email address is required</span>
            )}

            {errors.email && errors.email.type === 'pattern' && (
              <span className={errorStyle}>
                Please enter a valid email address
              </span>
            )}

            {errors.email && errors.email.type === 'api' && (
              <span className={errorStyle}>{errors.email.message}</span>
            )}
          </div>
          <Button
            type="submit"
            text="Send password reset instructions"
            buttonStyle="gradiented"
            loading={loading}
            disabled={loading}
          />
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordView;
